import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  fetchAvailableAssetFiles,
  fetchPublicAssetFile,
  getPublicDocumentError,
  PublicAssetFile,
} from "../../api/publicAssetDocumentsService";
import {
  isFolderId,
  PublicAssetDetails,
  PublicAssetType,
  readPublicAssetDetails,
} from "../../helpers/publicAssetDocuments";

const displayDate = (value?: string | null) => {
  if (!value) return "Not specified";
  // The date component is kept unchanged by the visitor's time zone.
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? "Not specified"
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const DocumentPreview = ({
  file,
  onClose,
  onDownload,
  downloading,
  downloadError,
}: {
  file: PublicAssetFile;
  onClose: () => void;
  onDownload: (file: PublicAssetFile) => void;
  downloading: boolean;
  downloadError: string;
}) => {
  const [url, setUrl] = useState("");
  const preview = useQuery({
    queryKey: ["publicAssetDocumentPreview", file.id, file.fileName],
    queryFn: ({ signal }) => fetchPublicAssetFile(file, signal),
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (!preview.data) return;
    const objectUrl = URL.createObjectURL(preview.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [preview.data]);

  const mime = preview.data?.type ?? "";
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 7 }}>
        {file.type || "Document"}
        <IconButton
          aria-label="Close preview"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 240, p: { xs: 1, sm: 2 } }}>
        {downloadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {downloadError}
          </Alert>
        )}
        {preview.isPending && (
          <Stack alignItems="center" spacing={2} sx={{ py: 8 }} role="status">
            <CircularProgress />
            <Typography>Loading preview…</Typography>
          </Stack>
        )}
        {preview.isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => preview.refetch()}>
                Retry
              </Button>
            }
          >
            {getPublicDocumentError(preview.error)}
          </Alert>
        )}
        {!!url && mime === "application/pdf" && (
          <Box
            component="iframe"
            src={url}
            title={`${file.type} preview`}
            sx={{ width: "100%", height: "65vh", border: 0 }}
          />
        )}
        {!!url && ["image/jpeg", "image/png"].includes(mime) && (
          <Box
            component="img"
            src={url}
            alt={file.type || ""}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "65vh",
              mx: "auto",
              objectFit: "contain",
            }}
          />
        )}
        {!!url &&
          !["application/pdf", "image/jpeg", "image/png"].includes(mime) && (
            <Alert severity="info">
              A preview is not available for this file type. Download the
              document to open it.
            </Alert>
          )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          disabled={downloading}
          onClick={() => onDownload(file)}
        >
          {downloading ? "Downloading…" : "Download"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PublicAssetDocumentsContent = ({
  assetType,
  folderId,
  details,
}: {
  assetType?: string;
  folderId?: string;
  details: PublicAssetDetails;
}) => {
  const [folderOpen, setFolderOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<PublicAssetFile | null>(
    null,
  );
  const [downloadId, setDownloadId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState("");
  const activeDownload = useRef<AbortController | null>(null);
  const validLink =
    (assetType === "truck" || assetType === "trailer") && isFolderId(folderId);
  const files = useQuery({
    queryKey: ["publicAssetFiles", assetType, folderId],
    queryFn: ({ signal }) =>
      fetchAvailableAssetFiles(assetType as PublicAssetType, folderId!, signal),
    enabled: validLink,
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  useEffect(
    () => () => {
      activeDownload.current?.abort();
    },
    [],
  );

  const download = async (file: PublicAssetFile) => {
    if (activeDownload.current) return;
    const controller = new AbortController();
    activeDownload.current = controller;
    setDownloadId(file.id);
    setDownloadError("");
    try {
      const blob = await fetchPublicAssetFile(file, controller.signal);
      if (!controller.signal.aborted)
        saveAs(blob, file.fileName || `${file.type || "document"}-${file.id}`);
    } catch (error) {
      if (!controller.signal.aborted)
        setDownloadError(getPublicDocumentError(error));
    } finally {
      activeDownload.current = null;
      if (!controller.signal.aborted) setDownloadId(null);
    }
  };

  const summary = [details.year, details.make, details.model]
    .filter(Boolean)
    .join(" ");
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", pb: 6 }}>
      <Box
        component="header"
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={800} color="primary.main" variant="h6">
            C1 Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unit documents
          </Typography>
        </Container>
      </Box>
      <Container component="main" maxWidth="md" sx={{ pt: { xs: 3, sm: 5 } }}>
        {!validLink ? (
          <Alert severity="warning">
            This document link is invalid. Please request a new link from the
            sender.
          </Alert>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Chip
                label={assetType === "truck" ? "Truck" : "Trailer"}
                size="small"
                sx={{ mb: 1.5 }}
              />
              <Typography variant="h4" component="h1" fontWeight={700}>
                {details.unitNumber
                  ? `Unit ${details.unitNumber}`
                  : "Unit documents"}
              </Typography>
              {summary && (
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {summary}
                </Typography>
              )}
            </Box>
            <Paper
              variant="outlined"
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}
            >
              <Typography
                variant="h6"
                component="h2"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                Asset information
              </Typography>
              <Grid container spacing={2.5}>
                {(
                  [
                    ["Unit number", details.unitNumber],
                    ["Year", details.year],
                    ["Make", details.make],
                    ["Model", details.model],
                    ["VIN", details.vin],
                    ["Plate number", details.licensePlate],
                  ] as [string, string][]
                ).map(([label, value]) => (
                  <Grid item xs={12} sm={label === "VIN" ? 8 : 4} key={label}>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography
                      fontWeight={600}
                      sx={{ mt: 0.5, overflowWrap: "anywhere" }}
                    >
                      {value || "Not provided"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <Box
                sx={{
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="h6" component="h2" fontWeight={600}>
                  Documents
                </Typography>
                <Button
                  size="small"
                  disabled={files.isFetching}
                  onClick={() => files.refetch()}
                >
                  {files.isFetching && !files.isPending
                    ? "Refreshing…"
                    : "Refresh"}
                </Button>
              </Box>
              <Divider />
              {files.isPending && (
                <Stack
                  spacing={2}
                  alignItems="center"
                  sx={{ p: 5 }}
                  role="status"
                >
                  <CircularProgress size={30} />
                  <Typography color="text.secondary">
                    Loading documents…
                  </Typography>
                </Stack>
              )}
              {files.isError && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {getPublicDocumentError(files.error)}
                </Alert>
              )}
              {files.isSuccess && !folderOpen && (
                <Button
                  fullWidth
                  color="inherit"
                  aria-expanded={false}
                  onClick={() => setFolderOpen(true)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    p: { xs: 2, sm: 3 },
                    textTransform: "none",
                    gap: 2,
                  }}
                >
                  <FolderOutlinedIcon
                    sx={{ color: "primary.main", fontSize: 44 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>
                      {details.unitNumber
                        ? `Unit ${details.unitNumber} documents`
                        : "Available documents"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {files.data.length}{" "}
                      {files.data.length === 1 ? "document" : "documents"} ·
                      Open folder
                    </Typography>
                  </Box>
                  <ChevronRightIcon />
                </Button>
              )}
              {files.isSuccess && folderOpen && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setFolderOpen(false);
                      setDownloadError("");
                    }}
                    sx={{ mb: 2 }}
                  >
                    Back to folders
                  </Button>
                  {downloadError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      onClose={() => setDownloadError("")}
                    >
                      {downloadError}
                    </Alert>
                  )}
                  {!files.data.length ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                      <FolderOutlinedIcon
                        color="disabled"
                        sx={{ fontSize: 44 }}
                      />
                      <Typography fontWeight={600}>
                        No available documents
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                      >
                        There are currently no unexpired documents in this
                        folder.
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {files.data.map((file) => (
                        <Paper
                          variant="outlined"
                          key={file.id}
                          sx={{ p: 2, borderRadius: 2 }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ sm: "center" }}
                          >
                            <DescriptionOutlinedIcon
                              sx={{
                                color: "primary.main",
                                display: { xs: "none", sm: "block" },
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                fontWeight={600}
                                sx={{ overflowWrap: "anywhere" }}
                              >
                                {file.type || "Document"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Expires {displayDate(file.expirationDate)}
                              </Typography>
                            </Box>
                            <Stack
                              flexWrap={{ xs: "wrap", lg: "nowrap" }}
                              useFlexGap
                              direction="row"
                              spacing={1}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityOutlinedIcon />}
                                aria-label={`Preview ${file.type}`}
                                onClick={() => {
                                  setDownloadError("");
                                  setSelectedFile(file);
                                }}
                              >
                                Preview
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<DownloadOutlinedIcon />}
                                disabled={!!downloadId}
                                aria-label={`Download ${file.type}`}
                                onClick={() => download(file)}
                              >
                                {downloadId === file.id
                                  ? "Downloading…"
                                  : "Download"}
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </Paper>
          </Stack>
        )}
      </Container>
      {selectedFile && (
        <DocumentPreview
          key={selectedFile.id}
          file={selectedFile}
          onClose={() => setSelectedFile(null)}
          onDownload={download}
          downloading={!!downloadId}
          downloadError={downloadError}
        />
      )}
    </Box>
  );
};

const PublicAssetDocumentsPage = () => {
  const { assetType, folderId } = useParams<{
    assetType: string;
    folderId: string;
  }>();
  const { hash } = useLocation();
  const details = useMemo(() => readPublicAssetDetails(hash), [hash]);
  return (
    <PublicAssetDocumentsContent
      key={`${assetType}/${folderId}/${hash}`}
      assetType={assetType}
      folderId={folderId}
      details={details}
    />
  );
};

export default PublicAssetDocumentsPage;
